import { Injector } from '@angular/core';
import { draggableNodeStopEvent } from '@examdojo/core/tiptap';
import { DOMNode, mergeAttributes, Node } from '@tiptap/core';
import { DOMParser, Fragment } from '@tiptap/pm/model';
import { AngularNodeViewRenderer } from 'ngx-tiptap';
import { parseJsonAttr } from '../parse-json-attr.util';
import { FormulaCalloutNodeComponent } from './formula-callout-node.component';
import { DEFAULT_FORMULA_CALLOUT_PROPS, FORMULA_CALLOUT_NODE_NAME } from './formula-callout.model';

const formulaCalloutClassName = 'formula-callout';
const formulaDataAttr = 'data-formula';
const explanationDataAttr = 'data-explanation';
const showDerivationDataAttr = 'data-show-derivation';
const centerFormulaDataAttr = 'data-center-formula';
const formulaBookletDataAttr = 'data-formula-booklet';
const formulaBookletUrlDataAttr = 'data-formula-booklet-url';
const borderlessDataAttr = 'data-borderless';

export const FormulaCalloutExtension = (injector: Injector) => {
  return Node.create({
    name: FORMULA_CALLOUT_NODE_NAME,
    content: 'block+',
    group: 'block',
    defining: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        formula: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.formula,
          renderHTML: (attributes) => ({
            [formulaDataAttr]: JSON.stringify(attributes['formula'] ?? DEFAULT_FORMULA_CALLOUT_PROPS.formula),
          }),
          parseHTML: (element) => parseJsonAttr(element, formulaDataAttr, DEFAULT_FORMULA_CALLOUT_PROPS.formula),
        },
        explanation: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.explanation,
          renderHTML: (attributes) => ({
            [explanationDataAttr]: attributes['explanation'] ?? '',
          }),
          parseHTML: (element) => element.getAttribute(explanationDataAttr) ?? '',
        },
        show_derivation: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.show_derivation,
          renderHTML: (attributes) => ({
            [showDerivationDataAttr]: JSON.stringify(attributes['show_derivation'] ?? false),
          }),
          parseHTML: (element) => parseJsonAttr(element, showDerivationDataAttr, false),
        },
        center_formula: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.center_formula,
          renderHTML: (attributes) => ({
            [centerFormulaDataAttr]: JSON.stringify(attributes['center_formula'] ?? true),
          }),
          parseHTML: (element) => parseJsonAttr(element, centerFormulaDataAttr, true),
        },
        formula_booklet: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.formula_booklet,
          renderHTML: (attributes) => ({
            [formulaBookletDataAttr]: JSON.stringify(attributes['formula_booklet'] ?? false),
          }),
          parseHTML: (element) => parseJsonAttr(element, formulaBookletDataAttr, false),
        },
        formula_booklet_url: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.formula_booklet_url,
          renderHTML: (attributes) => ({
            [formulaBookletUrlDataAttr]: attributes['formula_booklet_url'] ?? '',
          }),
          parseHTML: (element) => element.getAttribute(formulaBookletUrlDataAttr) ?? '',
        },
        borderless: {
          default: DEFAULT_FORMULA_CALLOUT_PROPS.borderless,
          renderHTML: (attributes) => ({
            [borderlessDataAttr]: JSON.stringify(attributes['borderless'] ?? false),
          }),
          parseHTML: (element) => parseJsonAttr(element, borderlessDataAttr, false),
        },
        custom_css: {
          default: '',
        },
        custom_style: {
          default: null,
        },
        class: {
          renderHTML: (attributes) => {
            const otherClasses = ((attributes['class'] ?? '') as string)
              .split(' ')
              .filter((c) => c !== formulaCalloutClassName);
            return { class: `${formulaCalloutClassName} ${otherClasses.join(' ')}` };
          },
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: `div.${formulaCalloutClassName}`,
          getContent(node: DOMNode, schema) {
            if (!(node instanceof Element)) {
              const paragraphType = schema.nodes['paragraph'];
              const textContent = node.textContent || '';
              const paragraph = textContent
                ? paragraphType.create(null, schema.text(textContent))
                : paragraphType.create(null);
              return Fragment.from(paragraph);
            }

            const contentElement = node.querySelector('.formula-callout-derivation') ?? node;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = contentElement.innerHTML;
            const parsedContent = DOMParser.fromSchema(schema).parse(tempDiv);

            return parsedContent.content;
          },
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes({ class: formulaCalloutClassName }, HTMLAttributes), 0];
    },

    addNodeView() {
      return AngularNodeViewRenderer(FormulaCalloutNodeComponent, {
        injector,
        stopEvent: draggableNodeStopEvent,
      });
    },
  });
};
